import { useEffect, useState } from "react";
import { ShieldAlert, LogOut } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

/**
 * Persistent yellow banner that appears at the top of every authenticated
 * page whenever the admin is operating as another user via the "Login As
 * User" admin action. Clicking "Exit impersonation" restores the admin's
 * original token from `rv_admin_token_backup` (set by AdminLandlords.jsx)
 * and hard-redirects back to the Platform Users admin page.
 *
 * If the backup token is missing for any reason (e.g., the admin manually
 * cleared localStorage), the banner stays hidden — there's nothing safe
 * we can do without forcing a re-login, which would surprise users.
 */
export default function ImpersonationBanner() {
  const { user } = useAuth();
  const [hasBackup, setHasBackup] = useState(false);

  useEffect(() => {
    setHasBackup(!!localStorage.getItem("rv_admin_token_backup"));
  }, [user?.id]);

  if (!hasBackup || !user) return null;

  const exit = () => {
    const adminToken = localStorage.getItem("rv_admin_token_backup");
    if (!adminToken) {
      toast.error("No admin token backup found — please sign in again.");
      return;
    }
    localStorage.setItem("rv_token", adminToken);
    localStorage.removeItem("rv_admin_token_backup");
    window.location.href = "/dashboard/admin/landlords";
  };

  return (
    <div
      className="bg-amber-100 border-b-2 border-amber-300 text-amber-900 print:hidden"
      data-testid="impersonation-banner"
      role="alert"
    >
      <div className="max-w-screen-2xl mx-auto px-4 py-2 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2 text-sm">
          <ShieldAlert className="w-4 h-4 shrink-0" />
          <span>
            <span className="font-semibold">Impersonation mode</span> — signed in as{" "}
            <span className="font-mono">{user?.email}</span>
            {user?.company ? (
              <span className="text-amber-700"> · {user.company}</span>
            ) : null}
            . Every action you take is recorded in the admin audit log.
          </span>
        </div>
        <button
          onClick={exit}
          className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest bg-white hover:bg-amber-50 border border-amber-300 rounded-sm px-2.5 py-1 transition-colors"
          data-testid="impersonation-exit-btn"
        >
          <LogOut className="w-3 h-3" /> Exit impersonation
        </button>
      </div>
    </div>
  );
}
